import React, {FC, PropsWithChildren} from 'react';
import styled, {css} from 'styled-components';

import {PaletteColorsEnum} from '../../../../../core/src/models/api/paletteModel';
import {fontWeights} from '../../../styles/fontStyles';
import {ellipsis} from '../../../styles/mixins';
import {useUserTheme} from '../../../theme/userThemeProvider';
import {TooltipCoordinator, TooltipCoordinatorProps} from '../../tooltips/tooltipCoordinator';
import {DROPDOWN_ITEM_DISABLED_OPACITY} from './dropdownItem';

/*
 * Props.
 */

export interface DropdownItemContentProps {
  isChecked?: boolean;
  isBold?: boolean;
  className?: string;
  isEditingWorkspace?: boolean;
  isDisabled?: boolean;
  color?: PaletteColorsEnum;
  renderTooltip?: TooltipCoordinatorProps['render'];
  plainTooltip?: TooltipCoordinatorProps['plainTooltip'];
  isTooltipPersistent?: TooltipCoordinatorProps['persistent'];
}

/*
 * Style.
 */

interface ContentStyleProps {
  $isBold?: boolean;
  $isDisabled?: boolean;
  $color?: PaletteColorsEnum;
  $hasTooltip?: boolean;
}
const StyledContentDiv = styled.div<ContentStyleProps>`
  grid-area: content;
  max-width: 100%;
  min-height: 20px;

  color: ${(p) => (p.$color && p.theme.palette[p.$color].shade40) || p.theme.greys.shade80};
  font-weight: ${(p) => (p.$isBold ? fontWeights.semibold : fontWeights.normal)};
  line-height: 20px;
  opacity: ${(p) => (p.$isDisabled ? DROPDOWN_ITEM_DISABLED_OPACITY : 1)};
  ${ellipsis()};

  ${(p) => maybeAddTooltip(p.$hasTooltip)}
`;

function maybeAddTooltip(hasTooltip?: boolean) {
  if (hasTooltip) {
    return css`
      display: flex;
      align-items: center;
    `;
  }

  return undefined;
}

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  margin-left: 6px;
`;

/*
 * Component.
 */

export const DropdownItemContent: FC<PropsWithChildren<DropdownItemContentProps>> = (props) => {
  const {
    isChecked,
    isBold,
    isEditingWorkspace,
    isDisabled,
    children,
    className,
    color,
    renderTooltip,
    plainTooltip,
    isTooltipPersistent,
  } = props;
  const renderAsBold = Boolean(isBold || isChecked);
  const hasTooltip = Boolean(renderTooltip || plainTooltip);

  const theme = useUserTheme();

  return (
    <StyledContentDiv
      $isBold={renderAsBold && !isEditingWorkspace}
      $isDisabled={isDisabled}
      $hasTooltip={hasTooltip}
      className={className}
      $color={color}
    >
      {children}
      {hasTooltip && (
        <StyledTooltipCoordinator
          theme={theme}
          render={renderTooltip}
          persistent={isTooltipPersistent}
          plainTooltip={plainTooltip}
        />
      )}
    </StyledContentDiv>
  );
};
